<template>
  <div
    class="flex flex-col gap-4 w-full max-w-[400px] mx-auto p-4 items-center justify-center box-border relative md:max-w-[500px] lg:max-w-[600px]"
  >
    <div
      class="pickers w-full flex flex-col gap-4 items-center justify-center md:flex-row md:gap-6"
    >
      <div class="w-full flex flex-col items-center gap-2">
        <button
          ref="vowelButton"
          :disabled="!vowelEnabled || !isPlayerTurn"
          :aria-label="vowelAriaLabel"
          :aria-describedby="'vowel-constraints'"
          class="selection-button vowel-button"
          :class="{
            'selection-button--loading': isVowelLoading,
            'selection-button--disabled': !vowelEnabled || !isPlayerTurn,
          }"
          @click="handleVowelClick"
          @keydown="handleKeydown($event, 'Vowel')"
        >
          <span v-if="!isVowelLoading">{{ STRINGS.ui.buttons.vowel }}</span>
          <div v-else class="flex items-center justify-center gap-2">
            <span>{{ STRINGS.ui.buttons.vowel }}</span>
            <div class="loading-spinner"></div>
          </div>
        </button>
        <div v-if="!vowelEnabled && isPlayerTurn" class="text-center p-2">
          <span class="text-sm text-arena-text-dim italic font-medium">{{
            vowelDisabledReason
          }}</span>
        </div>
      </div>

      <div class="w-full flex flex-col items-center gap-2">
        <button
          ref="consonantButton"
          :disabled="!consonantEnabled || !isPlayerTurn"
          :aria-label="consonantAriaLabel"
          :aria-describedby="'consonant-constraints'"
          class="selection-button consonant-button"
          :class="{
            'selection-button--loading': isConsonantLoading,
            'selection-button--disabled': !consonantEnabled || !isPlayerTurn,
          }"
          @click="handleConsonantClick"
          @keydown="handleKeydown($event, 'Consonant')"
        >
          <span v-if="!isConsonantLoading">{{ STRINGS.ui.buttons.consonant }}</span>
          <div v-else class="flex items-center justify-center gap-2">
            <span>{{ STRINGS.ui.buttons.consonant }}</span>
            <div class="loading-spinner"></div>
          </div>
        </button>
        <div v-if="!consonantEnabled && isPlayerTurn" class="text-center p-2">
          <span class="text-sm text-arena-text-dim italic font-medium">{{
            consonantDisabledReason
          }}</span>
        </div>
      </div>
    </div>

    <!-- Server auto-deal: ask the server to fill the remaining slots at once,
         mirroring the selection-timer-expiry flow (issue #360). Only shown as an
         enabled affordance on the selector's turn. -->
    <div class="auto-row w-full flex flex-col items-center gap-1.5">
      <span class="text-sm italic text-arena-text-muted select-none">{{
        STRINGS.ui.buttons.autoSelectSeparator
      }}</span>
      <button
        ref="autoButton"
        :disabled="!isPlayerTurn || isAutoLoading"
        :aria-label="STRINGS.aria.labels.autoSelectLetters"
        class="auto-deal-button"
        :class="{
          'auto-deal-button--disabled': !isPlayerTurn || isAutoLoading,
          'auto-deal-button--done': isAutoLoading,
        }"
        @click="handleAutoClick"
        @keydown="handleAutoKeydown"
      >
        {{ isAutoLoading ? STRINGS.ui.buttons.autoSelectDone : STRINGS.ui.buttons.autoSelect }}
      </button>
    </div>

    <div id="vowel-constraints" class="sr-only">
      {{ vowelConstraintText }}
    </div>
    <div id="consonant-constraints" class="sr-only">
      {{ consonantConstraintText }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { ANIMATION_CONFIG } from '@/config/constants';
import { STRINGS } from '@/constants/strings';
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts';
import type { SelectionStatus, LetterKind } from '@/types/generated';

interface Props {
  vowelEnabled: boolean;
  consonantEnabled: boolean;
  vowelCount: number;
  consonantCount: number;
  isPlayerTurn: boolean;
  status: SelectionStatus;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  selectLetter: [letterType: LetterKind];
  autoSelect: [];
}>();

// Loading states for button feedback
const isVowelLoading = ref(false);
const isConsonantLoading = ref(false);
const isAutoLoading = ref(false);

// Computed aria labels for accessibility
const vowelAriaLabel = computed(() => {
  return STRINGS.templates.vowelAriaLabel(props.vowelCount, props.vowelEnabled, props.isPlayerTurn);
});

const consonantAriaLabel = computed(() => {
  return STRINGS.templates.consonantAriaLabel(
    props.consonantCount,
    props.consonantEnabled,
    props.isPlayerTurn
  );
});

// Constraint text for screen readers
const vowelConstraintText = computed(() => {
  if (props.vowelCount >= 5) return STRINGS.ui.constraints.maximumVowels;
  return STRINGS.templates.vowelConstraint(props.vowelCount);
});

const consonantConstraintText = computed(() => {
  if (props.consonantCount >= 6) return STRINGS.ui.constraints.maximumConsonants;
  return STRINGS.templates.consonantConstraint(props.consonantCount);
});

// Disabled reason messages for visible indicators
const vowelDisabledReason = computed(() => {
  if (props.vowelCount >= 5) {
    return STRINGS.ui.constraints.allVowelsSelected;
  }
  return STRINGS.ui.constraints.vowelDisabled;
});

const consonantDisabledReason = computed(() => {
  if (props.consonantCount >= 6) {
    return STRINGS.ui.constraints.allConsonantsSelected;
  }
  return STRINGS.ui.constraints.consonantDisabled;
});

// Shared click handler with loading states
const handleLetterClick = (
  kind: LetterKind,
  isEnabled: boolean,
  isLoading: typeof isVowelLoading
) => {
  if (!isEnabled || !props.isPlayerTurn) return;

  isLoading.value = true;

  // Brief loading state for user feedback
  setTimeout(() => {
    isLoading.value = false;
  }, ANIMATION_CONFIG.BUTTON_FEEDBACK_DURATION);

  emit('selectLetter', kind);
};

const handleVowelClick = () => handleLetterClick('Vowel', props.vowelEnabled, isVowelLoading);
const handleConsonantClick = () =>
  handleLetterClick('Consonant', props.consonantEnabled, isConsonantLoading);

// Server auto-deal: ask the server to fill the remaining letters at once.
const handleAutoClick = () => {
  if (!props.isPlayerTurn || isAutoLoading.value) return;

  isAutoLoading.value = true;

  // Brief pressed state; on success the phase transitions before this clears.
  // Clearing lets the player retry if the request is dropped.
  setTimeout(() => {
    isAutoLoading.value = false;
  }, ANIMATION_CONFIG.BUTTON_FEEDBACK_DURATION);

  emit('autoSelect');
};

const handleAutoKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handleAutoClick();
  }
};

// Keyboard shortcuts
const handleKeydown = (event: KeyboardEvent, letterType: LetterKind) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    if (letterType === 'Vowel') {
      handleVowelClick();
    } else {
      handleConsonantClick();
    }
  }
};

// Keyboard shortcuts composable
const keyboardShortcuts = useKeyboardShortcuts({ enableDefaultShortcuts: false });

// Setup keyboard shortcuts for letter selection
const setupKeyboardShortcuts = () => {
  keyboardShortcuts.setContext('letter-selection');

  // Quick vowel selection (V key)
  keyboardShortcuts.registerShortcut({
    id: 'select-vowel',
    key: 'v',
    description: 'Select vowel letter',
    context: 'letter-selection',
    handler: () => {
      if (props.vowelEnabled && props.isPlayerTurn) {
        handleVowelClick();
      }
    },
  });

  // Quick consonant selection (C key)
  keyboardShortcuts.registerShortcut({
    id: 'select-consonant',
    key: 'c',
    description: 'Select consonant letter',
    context: 'letter-selection',
    handler: () => {
      if (props.consonantEnabled && props.isPlayerTurn) {
        handleConsonantClick();
      }
    },
  });

  // Alternative vowel selection (1 key)
  keyboardShortcuts.registerShortcut({
    id: 'select-vowel-alt',
    key: '1',
    description: 'Select vowel letter (alternative)',
    context: 'letter-selection',
    handler: () => {
      if (props.vowelEnabled && props.isPlayerTurn) {
        handleVowelClick();
      }
    },
  });

  // Alternative consonant selection (2 key)
  keyboardShortcuts.registerShortcut({
    id: 'select-consonant-alt',
    key: '2',
    description: 'Select consonant letter (alternative)',
    context: 'letter-selection',
    handler: () => {
      if (props.consonantEnabled && props.isPlayerTurn) {
        handleConsonantClick();
      }
    },
  });

  // Quick auto-select (A key) — let the server deal the rest
  keyboardShortcuts.registerShortcut({
    id: 'auto-select-letters',
    key: 'a',
    description: 'Let the game pick the remaining letters',
    context: 'letter-selection',
    handler: () => {
      if (props.isPlayerTurn) {
        handleAutoClick();
      }
    },
  });

  // Arrow key navigation between buttons
  keyboardShortcuts.registerShortcut({
    id: 'navigate-left',
    key: 'ArrowLeft',
    description: 'Focus vowel button',
    context: 'letter-selection',
    handler: () => {
      const vowelBtn = document.querySelector('.vowel-button') as HTMLButtonElement;
      if (vowelBtn && !vowelBtn.disabled) {
        vowelBtn.focus();
      }
    },
  });

  keyboardShortcuts.registerShortcut({
    id: 'navigate-right',
    key: 'ArrowRight',
    description: 'Focus consonant button',
    context: 'letter-selection',
    handler: () => {
      const consonantBtn = document.querySelector('.consonant-button') as HTMLButtonElement;
      if (consonantBtn && !consonantBtn.disabled) {
        consonantBtn.focus();
      }
    },
  });
};

onMounted(() => {
  setupKeyboardShortcuts();
});
</script>

<style scoped>
.selection-button {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 1rem 1.5rem;
  border: 2px solid var(--color-energy-primary);
  border-radius: 8px;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    transform 150ms cubic-bezier(0, 0, 0.2, 1),
    background-color 150ms cubic-bezier(0, 0, 0.2, 1),
    border-color 150ms cubic-bezier(0, 0, 0.2, 1),
    color 150ms cubic-bezier(0, 0, 0.2, 1),
    box-shadow 150ms cubic-bezier(0, 0, 0.2, 1),
    opacity 150ms cubic-bezier(0, 0, 0.2, 1);
  width: 100%;
  position: relative;
  overflow: hidden;
  font-family: var(--font-mono);
  letter-spacing: 0.05em;
  background: var(--color-arena-surface);
  color: var(--color-energy-primary);
  box-shadow: 0 0 1px var(--color-energy-primary);
}

.selection-button:hover:not(.selection-button--disabled):not(.selection-button--loading) {
  background: rgba(201, 168, 76, 0.15);
  border-color: var(--color-energy-secondary);
  color: var(--color-energy-secondary);
  transform: translateY(-2px);
  box-shadow: 0 0 3px var(--color-energy-secondary);
}

.selection-button:active:not(.selection-button--disabled):not(.selection-button--loading) {
  transform: translateY(0) scale(0.98);
  transition-duration: 100ms;
}

.selection-button:focus {
  outline: none;
  box-shadow: 0 0 5px var(--color-energy-primary);
}

.selection-button--disabled {
  background: var(--color-arena-void);
  color: var(--color-arena-text-dim);
  border-color: var(--color-arena-border);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
  opacity: 0.5;
}

.selection-button--loading {
  cursor: wait;
  transform: none;
}

/* Server auto-deal button: tertiary ghost affordance (design .auto-deal). */
.auto-deal-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0.7rem 1.5rem;
  border: 1px solid var(--color-arena-border);
  border-radius: 12px;
  background: transparent;
  color: var(--color-arena-text-muted);
  font-family: var(--font-sans);
  font-weight: 600;
  font-size: 0.8125rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    transform 200ms cubic-bezier(0, 0, 0.2, 1),
    color 300ms,
    border-color 300ms,
    background 300ms;
}

.auto-deal-button:hover:not(.auto-deal-button--disabled) {
  transform: translateY(-1px);
  color: var(--color-energy-secondary);
  border-color: var(--color-energy-secondary);
  background: rgba(91, 127, 164, 0.08);
}

.auto-deal-button:active:not(.auto-deal-button--disabled) {
  transform: translateY(0) scale(0.98);
}

.auto-deal-button:focus {
  outline: none;
  box-shadow: 0 0 5px var(--color-energy-secondary);
}

.auto-deal-button--disabled {
  opacity: 0.35;
  cursor: not-allowed;
  transform: none;
}

.auto-deal-button--done {
  color: var(--color-energy-success);
  border-color: rgba(0, 196, 143, 0.5);
  background: rgba(0, 196, 143, 0.08);
  opacity: 1;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@media (min-width: 768px) {
  .selection-button {
    min-height: 60px;
    font-size: 1.25rem;
  }
}

@media (min-width: 1024px) {
  .selection-button {
    min-height: 80px;
    max-width: 280px;
    font-size: 1.25rem;
  }
}

@media (prefers-contrast: high) {
  .selection-button--disabled {
    border: 2px dashed currentColor;
    opacity: 0.7;
  }
}

@media (prefers-reduced-motion: reduce) {
  .selection-button {
    transition: none;
  }

  .selection-button:hover:not(.selection-button--disabled) {
    transform: none;
  }

  .selection-button:active:not(.selection-button--disabled) {
    transform: none;
  }

  .loading-spinner {
    animation: none;
  }
}

@media (hover: none) {
  .selection-button:hover {
    transform: none;
    box-shadow: 0 2px 8px rgba(201, 168, 76, 0.2);
  }
}
</style>
